import store from '@/store';
import OSS from 'ali-oss';
import { v4 as uuidv4 } from 'uuid';
import {
  getAliyunOSSConfigRead,
  getAliyunOSSConfigWrite,
  getAliyunOSSConfigUnauthRead,
  getAliyunOSSConfigUnauthWrite,
} from '@/api/common';
import { checkIfExpired } from '@/libs/utils';

// 默认上传文件目录
export const defaultUploadDir = 'images';

// 默认阿里云OSS服务器时区
export const defaultServerTimezone = 'Asia/Hong_Kong';

// 默认Region
export const defaultRegion = process.env?.VUE_APP_REGION || 'oss-cn-shenzhen';

// 默认bucket
export const defaultBucket = process.env?.VUE_APP_BUCKET || 'infy-mbe-test';

// 固件bucket
export const firmwareBucket = process.env?.VUE_APP_BUCKET_UPDT;
console.log('defaultBucket', defaultBucket);
console.log('firmwareBucket', firmwareBucket);

// 在客户端使用临时访问凭证初始化OSS客户端，用于临时授权访问OSS资源。
export const aliyunClient = async ({ write = false, uploadBucket, logined = true, callback }) => {
  // 是否有指定bucket
  const currentUploadBucket = uploadBucket || defaultBucket;

  // 判断是否登录
  // 判断是否为读图片还是上传图片｜附件
  let tokenAPI;
  // 读取缓存配置
  let ossConfig;

  if (logined) {
    if (write) {
      // 上传
      tokenAPI = getAliyunOSSConfigWrite;

      // 读取缓存配置
      if (
        store.state?.ossConfig &&
        store.state?.ossConfig['logined'] &&
        store.state?.ossConfig['logined'][currentUploadBucket] &&
        store.state?.ossConfig['logined'][currentUploadBucket]['write']
      ) {
        // 读取缓存配置
        ossConfig = store.state?.ossConfig['logined'][currentUploadBucket]['write'];
      }
    } else {
      // 读取
      tokenAPI = getAliyunOSSConfigRead;

      // 读取缓存配置
      if (
        store.state?.ossConfig &&
        store.state?.ossConfig['logined'] &&
        store.state?.ossConfig['logined'][currentUploadBucket] &&
        store.state?.ossConfig['logined'][currentUploadBucket]['read']
      ) {
        // 读取缓存配置
        ossConfig = store.state?.ossConfig['logined'][currentUploadBucket]['read'];
      }
    }
  } else {
    if (write) {
      // 上传
      tokenAPI = getAliyunOSSConfigUnauthWrite;
      // 读取缓存配置
      if (
        store.state?.ossConfig &&
        store.state?.ossConfig['unlogined'] &&
        store.state?.ossConfig['unlogined'][currentUploadBucket] &&
        store.state?.ossConfig['unlogined'][currentUploadBucket]['write']
      ) {
        // 读取缓存配置
        ossConfig = store.state?.ossConfig['unlogined'][currentUploadBucket]['write'];
      }
    } else {
      // 读取
      tokenAPI = getAliyunOSSConfigUnauthRead;

      // 读取缓存配置
      if (
        store.state?.ossConfig &&
        store.state?.ossConfig['unlogined'] &&
        store.state?.ossConfig['unlogined'][currentUploadBucket] &&
        store.state?.ossConfig['unlogined'][currentUploadBucket]['read']
      ) {
        // 读取缓存配置
        ossConfig = store.state?.ossConfig['unlogined'][currentUploadBucket]['read'];
      }
    }
  }

  // 判断sts是否已经过期，没过期，则返回缓存的client(此处需要注意：服务器的时间应该在香港，则设置Asia/Hong_Kong)
  if (
    ossConfig &&
    ossConfig.expiration &&
    !checkIfExpired(ossConfig.expiration, defaultServerTimezone)
  ) {
    const newClient = new OSS({
      // yourRegion填写Bucket所在地域。以华东1（杭州）为例，yourRegion填写为oss-cn-hangzhou。
      region: defaultRegion,
      // 填写Bucket名称。
      bucket: currentUploadBucket,
      accessKeyId: ossConfig.accessKeyId,
      accessKeySecret: ossConfig.accessKeySecret,
      stsToken: ossConfig.securityToken,
      authorizationV4: true,
      // 刷新临时访问凭证。
      refreshSTSToken: async () => {
        const refreshToken = await tokenAPI();
        return {
          accessKeyId: refreshToken.accessKeyId,
          accessKeySecret: refreshToken.accessKeySecret,
          stsToken: refreshToken.securityToken,
        };
      },
    });

    callback(newClient);
    return;
  }

  // 未有缓存的时候，重新获取token
  await tokenAPI().then(token => {
    // 记录获取token的时间
    store.commit('setOssClientStartTime', Date.now());

    // 暂存OSS客户端
    if (token && token.data && token.data.expiration) {
      const currentUploadBucketConfig = {};

      if (logined) {
        if (write) {
          // 上传
          currentUploadBucketConfig['logined'] = {
            [currentUploadBucket]: {
              write: token.data,
            },
          };
        } else {
          // 读取
          currentUploadBucketConfig['logined'] = {
            [currentUploadBucket]: {
              read: token.data,
            },
          };
        }
      } else {
        // 未登录
        if (write) {
          currentUploadBucketConfig['unlogined'] = {
            [currentUploadBucket]: {
              write: token.data,
            },
          };
        } else {
          currentUploadBucketConfig['unlogined'] = {
            [currentUploadBucket]: {
              read: token.data,
            },
          };
        }
      }
      store.commit('setOssConfig', currentUploadBucketConfig);
    }

    const client = new OSS({
      // yourRegion填写Bucket所在地域。以华东1（杭州）为例，yourRegion填写为oss-cn-hangzhou。
      region: defaultRegion,
      // 填写Bucket名称。
      bucket: currentUploadBucket,
      accessKeyId: token.data.accessKeyId,
      accessKeySecret: token.data.accessKeySecret,
      stsToken: token.data.securityToken,
      authorizationV4: true,
      // 刷新临时访问凭证。
      refreshSTSToken: async () => {
        const refreshToken = await tokenAPI();
        return {
          accessKeyId: refreshToken.accessKeyId,
          accessKeySecret: refreshToken.accessKeySecret,
          stsToken: refreshToken.securityToken,
        };
      },
    });
    store.commit('setOssClient', client);
    callback(client);
  });
};

// 获取文件扩展名
export const getFileExtension = function(filename) {
  const index = filename.lastIndexOf('.');
  return index !== -1 ? filename.substring(index + 1) : '';
};

// 判断文件名是否重复
// objectKey->完整的文件路径（不包含域名）
async function checkIfFileExists(client, objectKey) {
  try {
    // 使用 head 方法查看文件元信息，如果文件存在，不会抛出异常
    return await client.head(objectKey);
  } catch (err) {
    if (err.code === 'NoSuchKey') {
      // console.log('报的404错误，是检查文件名是否存在，不存在才好上传，存在则文件名要改变+1');
      // console.log('文件不存在:', objectKey);
      return false;
    }
    console.error('检查文件是否存在时出错:', err);
    throw err;
  }
}

// 检查并返回存在的唯一的文件名
async function generateUniqueFileName(client, orignFileName) {
  // 文件夹名称
  let folderName = defaultUploadDir;
  let fileName = orignFileName;
  if (orignFileName.indexOf('/') > -1) {
    folderName = orignFileName.split('/')[0];
    fileName = orignFileName.split('/')[1];
  }

  // 获取文件后缀
  const extension = getFileExtension(fileName);

  // 获取一个初始的文件名(不带后缀)
  const timestamp = Date.now();
  let objectKey = `${uuidv4()}-${timestamp}`;

  let counter = 1;
  // 检查objectKey是不是存在
  // console.log('报的404错误，是检查文件名是否存在，不存在才好上传，存在则文件名要改变+1');
  while (await checkIfFileExists(client, `${objectKey}.${extension}`)) {
    // 如果文件名存在，则在原来的文件名上加上'-1'
    objectKey = `${objectKey}-${counter++}.${extension}`;
  }

  // 返回最终的唯一文件名
  return `${folderName}/${objectKey}.${extension}`;
}

// 多个文件上传
export async function uploadMultipleFiles({ files, uploadBucket, callback }) {
  // bucket，fileName，是否登录，回调函数
  aliyunClient({
    // 所有上传都是写入操作
    write: true,
    uploadBucket,
    logined: true,
    callback: async client => {
      try {
        // 创建每个文件的上传 Promise
        const uploadPromises = files.map(async file => {
          try {
            // 生成唯一文件名
            const uniqueFileName = await generateUniqueFileName(client, file.name);
            // 记录上传的uuidname
            file.uuidName = uniqueFileName;

            // 设置header
            const headers = {};
            // 设置下载的文件名，如固件需要固定下载文件名为nccu_app.rpkg
            if (file.downloadFileName) {
              headers['Content-Disposition'] = `attachment; filename="${file.downloadFileName}"`;
            }

            // 上传文件并返回上传结果
            const uploadResponse = await client.put(uniqueFileName, file.originFileObj || file, {
              headers,
            });

            file.result = uploadResponse;
            return uploadResponse;
          } catch (err) {
            // 捕获错误并调用回调函数
            callback && callback(false, err);
            console.error('上传文件时出错:', err);
            throw err; // 重新抛出错误以便 Promise.all 处理
          }
        });

        // 并行上传所有文件
        const results = await Promise.all(uploadPromises);
        callback && callback(true, results); // 成功时回调所有结果
      } catch (err) {
        // 捕获并行上传中的任何错误
        callback && callback(false, err);
        console.error('上传失败：', err);
      }
    },
  });
}

// 获取文件列表
// expirationTime： 默认设置 URL 过期时间为 1 小时
export async function generateSignedUrl({
  objectKey,
  expirationTime = 1 * 3600,
  logined = true,
  uploadBucket,
  shortUrl = false,
  // 如果某个读的操作要求写入权限，则需要传入write=true
  write = false,
}) {
  if (!objectKey) {
    console.error('url error');
    return null;
  }

  const _objectKey = getFileNameFromUrl(objectKey);
  if (!_objectKey) {
    return null;
  }
  let client = store.state.ossClient;
  // 时间超过半小时，则主动刷新
  if (!client || Date.now() - store.state.ossClientStartTime > store.state.ossClientFreshInterval) {
    await aliyunClient({
      write,
      uploadBucket,
      logined,
      callback: function(_client) {
        client = _client;
      },
    });
  }

  try {
    // 生成签名 URL
    const url = client.signatureUrl(_objectKey, {
      expires: expirationTime,
    });
    console.log('生成的签名 URL:', url);

    // 如果shortUrl为true，则返回短链接
    if (shortUrl) {
      // 取？号前面的部分
      const index = url.indexOf('?');
      const shortUrl = url.substring(0, index);

      return shortUrl;
    }

    return url;
  } catch (err) {
    console.error('生成签名 URL 失败:', err);
    return null;
  }
}

// 下载文件
export async function downloadFile(objectKey, expirationTime = 1 * 3600, logined = true) {
  const url = await generateSignedUrl({ objectKey, expirationTime, logined, write: false });
  if (url) {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

// 从 URL 中提取文件名
export function getFileNameFromUrl(url) {
  // 判断URL
  if (!url || !/\./gi.test(url)) {
    return null;
  }

  // 数据库保留的文件名基本是不带http的相对路径，如果是完整路径需要去掉host，只取pathname
  const urlRegex = /^(https?:\/\/)[^\s/$.?#].[^\s]*$/;
  let finnallyURL = '';
  if (urlRegex.test(url)) {
    // 创建一个新的 URL 对象
    const parsedUrl = new URL(url);
    // 获取 pathname 部分
    const pathname = parsedUrl.pathname;
    finnallyURL = pathname;
  } else {
    finnallyURL = String(url).split('?')[0]; // 去掉参数部分
  }
  return finnallyURL;
}

// sts===============================================

// # OSS upload Attach
// oss.upload.region=oss-cn-shenzhen
// oss.upload.accessKeyId=LTAI5tSm9EC17WP4KKSRss3z
// oss.upload.accessKeySecret=bxMedweDOaDqbQljbldFJ0yGnkOV9x
// oss.upload.bucket=infy-mbe-test
// oss.upload.endpoint=oss-cn-shenzhen.aliyuncs.com
// oss.upload.secure=true
